import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserList } from './UserList';
import { UserForm } from './UserForm';
import { FileManager } from './FileManager';
import { ProjectList } from './ProjectList';
import { SetupWizard } from './SetupWizard';
import { BootcampLearning } from './BootcampLearning';

type TabView = 'overview' | 'bootcamp' | 'projects' | 'users' | 'files' | 'api';

export const MainApp: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<TabView>('overview');
  const [apiHealth, setApiHealth] = useState<'loading' | 'healthy' | 'error'>('loading');
  const [showSetupWizard, setShowSetupWizard] = useState(false);

  useEffect(() => {
    checkApiHealth();
  }, []);

  const checkApiHealth = async () => {
    try {
      const response = await fetch('/api/health');
      if (response.ok) {
        setApiHealth('healthy');
      } else {
        setApiHealth('error');
      }
    } catch (error) {
      setApiHealth('error');
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '🏠' },
    { id: 'bootcamp', name: 'Bootcamp', icon: '🏕️' },
    { id: 'projects', name: 'Projects', icon: '🤖' },
    { id: 'users', name: 'Users', icon: '👥' },
    { id: 'files', name: 'Files', icon: '📁' },
    { id: 'api', name: 'API', icon: '🔌' }
  ] as const;

  const features = [
    {
      title: 'CloudFlare Workers',
      description: 'Serverless functions running globally at the edge',
      icon: '⚡',
      status: 'Ready'
    },
    {
      title: 'D1 Database',
      description: 'Serverless SQLite database with automatic scaling',
      icon: '🗃️',
      status: apiHealth === 'healthy' ? 'Connected' : 'Checking...'
    },
    {
      title: 'R2 Storage',
      description: 'Object storage for files, images, and assets',
      icon: '📦',
      status: 'Ready'
    },
    {
      title: 'React Frontend',
      description: 'Modern React with TypeScript and Tailwind CSS',
      icon: '⚛️',
      status: 'Active'
    }
  ];

  const renderTabContent = () => {
    switch (currentTab) {
      case 'bootcamp':
        return <BootcampLearning
          onComplete={(conceptId) => console.log('Completed concept:', conceptId)}
          onProgress={(conceptId, progress) => console.log('Progress:', conceptId, progress)}
        />;
      case 'projects':
        return <ProjectList />;
      case 'users':
        return (
          <div className="space-y-6">
            <UserForm
              onSave={() => {}}
              onCancel={() => {}}
              onNarrative={() => {}}
            />
            <UserList
              onEdit={() => {}}
              refreshTrigger={0}
              onNarrative={() => {}}
            />
          </div>
        );
      case 'files':
        return <FileManager />;
      case 'api':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">API Endpoints</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="font-mono text-sm">GET /api/health</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    apiHealth === 'healthy' ? 'bg-green-100 text-green-800' :
                    apiHealth === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {apiHealth === 'healthy' ? 'Online' : apiHealth === 'error' ? 'Offline' : 'Checking'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="font-mono text-sm">GET /api/users</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">Ready</span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="font-mono text-sm">POST /api/users</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">Ready</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">Test API Connection</h3>
              <button
                onClick={checkApiHealth}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Test Connection
              </button>
            </div>
          </div>
        );
      default:
        return (
          <div className="space-y-8">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-4">🚀 Welcome to Your Application</h2>
              <p className="text-xl opacity-90 mb-6">
                Your CloudFlare full-stack starter template is ready to go!
              </p>
              <div className="text-lg opacity-80">
                Start building your AI-powered application with this production-ready foundation.
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-sm border p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-3xl">{feature.icon}</div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      feature.status === 'Connected' || feature.status === 'Active' || feature.status === 'Ready'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {feature.status}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Quick Start */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">🚀 Quick Start</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                  <span>Check out <strong>Projects</strong> to manage your AI applications</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                  <span>Explore the <strong>Users</strong> tab to see database CRUD operations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                  <span>Use the <strong>Files</strong> tab for file upload/storage examples</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                  <span>View the <strong>API</strong> tab to test backend connectivity</span>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">🤖</div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowSetupWizard(true)}
                className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Setup Wizard
              </button>
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                apiHealth === 'healthy' ? 'bg-green-100 text-green-800' :
                apiHealth === 'error' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  apiHealth === 'healthy' ? 'bg-green-500' :
                  apiHealth === 'error' ? 'bg-red-500' :
                  'bg-yellow-500'
                }`} />
                <span>API {apiHealth === 'healthy' ? 'Online' : apiHealth === 'error' ? 'Offline' : 'Checking'}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setCurrentTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </main>

      {/* Setup Wizard Modal */}
      {showSetupWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto">
            <SetupWizard onComplete={() => setShowSetupWizard(false)} />
          </div>
        </div>
      )}
    </div>
  );
};